import { gql, useQuery } from '@apollo/client'
import _ from 'lodash'
import moment from 'moment'
import { useMemo } from 'react'

import { BRAND_LOCATION_GROUP_ID } from 'pared/constants/brands'
import useBrands from 'pared/layouts/hooks/useBrands'

import { useDateFilter } from '../../../dateFilter'
import { useGroupFilter } from '../../../groupFilter'
import { IApiDataType } from '../../types'

interface IDataType {
  getLocationDeliveryKpi: {
    nodes: {
      locationId: number
      inaccurateOrderPercentRanking: number
      cancelledOrderPercentRanking: number
      delayedOrderPercentRanking: number
      orderWithAnyIssuePercentRanking: number
      avgCustomerReviewScoreRanking: number
      totalOrderCountRanking: number
      averageTripTimeRanking: number
    }[]
  }

  customizedRankLocationDelivery: {
    nodes: {
      locationId: number
      sumSubtotalRanking: number
    }[]
  }
}

const query = gql`
  query deliveryRankings(
    $iEndDate: Date!
    $iStartDate: Date!
    $iLocationId: Int!
    $iBrandId: Int!
  ) {
    getLocationDeliveryKpi(
      iLocationId: $iLocationId
      iStartDate: $iStartDate
      iEndDate: $iEndDate
    ) {
      nodes {
        locationId
        inaccurateOrderPercentRanking
        cancelledOrderPercentRanking
        delayedOrderPercentRanking
        orderWithAnyIssuePercentRanking
        avgCustomerReviewScoreRanking
        totalOrderCountRanking
        averageTripTimeRanking
      }
    }

    customizedRankLocationDelivery(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iFilter: {
        location_group_ids: [$iBrandId]
        bypass_row_level_security: true
      }
    ) {
      nodes {
        locationId
        sumSubtotalRanking
      }
    }
  }
`

export const bibibopDeliveryRankingConfigs = {
  sumSubtotalRanking: 'number',
  inaccurateOrderPercentRanking: 'number',
  cancelledOrderPercentRanking: 'number',
  delayedOrderPercentRanking: 'number',
  orderWithAnyIssuePercentRanking: 'number',
  avgCustomerReviewScoreRanking: 'number',
  totalOrderCountRanking: 'number',
  averageTripTimeRanking: 'number',
} as const

const YESTERDAY = moment().subtract(1, 'day')

const useBibibopDeliveryRanking = () => {
  const { startDate, endDate: filterEndDate } = useDateFilter()
  const { brand } = useBrands()
  const { groupFilter } = useGroupFilter()
  const endDate = useMemo(
    () =>
      moment(filterEndDate, 'YYYY-MM-DD').isAfter(YESTERDAY)
        ? YESTERDAY.format('YYYY-MM-DD')
        : filterEndDate,
    [filterEndDate],
  )
  const { data, loading } = useQuery<IDataType>(query, {
    variables: {
      iStartDate: startDate,
      iEndDate: endDate,
      iLocationId: groupFilter?.ids[0],
      iBrandId: BRAND_LOCATION_GROUP_ID[brand],
    },
    skip: !startDate || !endDate || !groupFilter,
  })

  return {
    data: useMemo((): IApiDataType => {
      const deliveryRanking = data?.getLocationDeliveryKpi.nodes[0]

      if (!deliveryRanking) return null

      const customDeliveryRanking =
        data?.customizedRankLocationDelivery.nodes.find(
          (n) => n.locationId === deliveryRanking.locationId,
        )

      return {
        ...deliveryRanking,
        ...customDeliveryRanking,
      }
    }, [data]),
    loading,
  }
}

export default useBibibopDeliveryRanking
